import { lazy, Suspense, type ReactNode } from 'react'
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom'
import { SiteLayout } from '@/components/site/SiteLayout'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { Skeleton } from '@/components/ui/misc'
import { AdminRoute, ExamRoute, ProtectedRoute, PublicOnlyRoute } from './ProtectedRoute'

/* Route-level code splitting for every route, and the ENTIRE admin portal in
   its own chunk — students never load it, and it is a large surface. */

/* Auth */
const LoginScreen = lazy(() =>
  import('@/features/auth/screens/LoginScreen').then((m) => ({ default: m.LoginScreen })),
)
const SignupScreen = lazy(() =>
  import('@/features/auth/screens/SignupScreen').then((m) => ({ default: m.SignupScreen })),
)
const ForgotPasswordScreen = lazy(() =>
  import('@/features/auth/screens/ForgotPasswordScreen').then((m) => ({
    default: m.ForgotPasswordScreen,
  })),
)
const AdminLoginScreen = lazy(() =>
  import('@/features/auth/screens/AdminLoginScreen').then((m) => ({ default: m.AdminLoginScreen })),
)

/* Public — the signed-out site: catalog plus the static pages the footer
   links to. They share one chunk; they are small and always reached together
   through the same chrome. */
const PublicLandingScreen = lazy(() =>
  import('@/features/catalog/screens/PublicLandingScreen').then((m) => ({
    default: m.PublicLandingScreen,
  })),
)
const InfoScreens = {
  Terms: lazy(() =>
    import('@/features/catalog/screens/InfoScreen').then((m) => ({ default: m.TermsScreen })),
  ),
  Privacy: lazy(() =>
    import('@/features/catalog/screens/InfoScreen').then((m) => ({ default: m.PrivacyScreen })),
  ),
  Contact: lazy(() =>
    import('@/features/catalog/screens/InfoScreen').then((m) => ({ default: m.ContactScreen })),
  ),
  About: lazy(() =>
    import('@/features/catalog/screens/InfoScreen').then((m) => ({ default: m.AboutScreen })),
  ),
  Cart: lazy(() =>
    import('@/features/catalog/screens/InfoScreen').then((m) => ({ default: m.CartScreen })),
  ),
}

/* Share links — the signed-out exam flow. Its own chunk: a participant on a
   link loads this and the exam, and nothing else in the app. */
const ExamLinkScreen = lazy(() =>
  import('@/features/quickexam/screens/ExamLinkScreen').then((m) => ({
    default: m.ExamLinkScreen,
  })),
)
const ExamSessionEndedScreen = lazy(() =>
  import('@/features/quickexam/screens/ExamLinkScreen').then((m) => ({
    default: m.ExamSessionEndedScreen,
  })),
)

/* Student */
const LandingScreen = lazy(() =>
  import('@/features/catalog/screens/LandingScreen').then((m) => ({ default: m.LandingScreen })),
)
const SeriesDetailScreen = lazy(() =>
  import('@/features/catalog/screens/SeriesDetailScreen').then((m) => ({
    default: m.SeriesDetailScreen,
  })),
)
const TestStartScreen = lazy(() =>
  import('@/features/catalog/screens/TestStartScreen').then((m) => ({ default: m.TestStartScreen })),
)
const ExamScreen = lazy(() =>
  import('@/features/exam/screens/ExamScreen').then((m) => ({ default: m.ExamScreen })),
)
const ResultScreen = lazy(() =>
  import('@/features/results/screens/ResultScreen').then((m) => ({ default: m.ResultScreen })),
)
const ReviewScreen = lazy(() =>
  import('@/features/results/screens/ReviewScreen').then((m) => ({ default: m.ReviewScreen })),
)
const LeaderboardScreen = lazy(() =>
  import('@/features/leaderboard/LeaderboardScreen').then((m) => ({
    default: m.LeaderboardScreen,
  })),
)
const FeedbackScreen = lazy(() =>
  import('@/features/feedback/FeedbackScreen').then((m) => ({ default: m.FeedbackScreen })),
)
const ProfileScreen = lazy(() =>
  import('@/features/profile/ProfileScreen').then((m) => ({ default: m.ProfileScreen })),
)

/* Admin — one dynamic import boundary for the whole portal. */
const AdminApp = lazy(() => import('@/features/admin/AdminApp'))

function RouteFallback(): ReactNode {
  return (
    <div style={{ padding: 'var(--s-6)', maxWidth: '900px', margin: '0 auto' }} aria-busy="true">
      <span className="visually-hidden">Loading…</span>
      <Skeleton height="28px" width="45%" />
      <div style={{ height: 'var(--s-4)' }} />
      <Skeleton height="160px" />
    </div>
  )
}

/** Every route sits inside a boundary that can re-render its own subtree. */
function RouteBoundary({ where }: { where: string }): ReactNode {
  return (
    <ErrorBoundary where={where}>
      <Suspense fallback={<RouteFallback />}>
        <Outlet />
      </Suspense>
    </ErrorBoundary>
  )
}

export const router = createBrowserRouter([
  {
    element: <RouteBoundary where="public" />,
    children: [
      /* The public site and the auth screens, all inside the shared chrome.
         Reachable whether or not the visitor has a session — the header adapts
         itself to whoever is signed in. */
      {
        element: <SiteLayout />,
        children: [
          { path: '/', element: <PublicLandingScreen /> },
          { path: '/terms', element: <InfoScreens.Terms /> },
          { path: '/privacy', element: <InfoScreens.Privacy /> },
          { path: '/contact', element: <InfoScreens.Contact /> },
          { path: '/about', element: <InfoScreens.About /> },
          { path: '/cart', element: <InfoScreens.Cart /> },
          {
            element: <PublicOnlyRoute audience="student" />,
            children: [
              { path: '/login', element: <LoginScreen /> },
              { path: '/signup', element: <SignupScreen /> },
              { path: '/verify', element: <Navigate to="/signup" replace /> },
              { path: '/set-password', element: <Navigate to="/signup" replace /> },
              { path: '/forgot-password', element: <ForgotPasswordScreen /> },
            ],
          },
          {
            element: <PublicOnlyRoute audience="admin" />,
            children: [{ path: '/admin/login', element: <AdminLoginScreen /> }],
          },
        ],
      },
    ],
  },

  /* ── Share links ─────────────────────────────────────────────────────
     Fully anonymous, outside every guard and outside SiteLayout: a participant
     arriving on a link has no session and no business seeing a nav bar that
     offers them a cart. `/e/expired` is static and so out-ranks the dynamic
     token below it. */
  {
    element: <RouteBoundary where="share-link" />,
    children: [
      { path: '/e/expired', element: <ExamSessionEndedScreen /> },
      { path: '/e/:token', element: <ExamLinkScreen /> },
    ],
  },

  /* ── The exam ────────────────────────────────────────────────────────
     Deliberately OUTSIDE SiteLayout: the exam is the one screen with no site
     chrome, because a header would give the student a nav bar to wander off
     into mid-test. Its error boundary never unmounts the exam store, so a
     render crash in one question cannot destroy answers.

     `ExamRoute` rather than `ProtectedRoute`: a share-link guest sits the same
     paper on the same screen. The backend decides whose attempt is whose. */
  {
    element: <RouteBoundary where="exam-route" />,
    children: [
      {
        element: <ExamRoute />,
        children: [{ path: '/attempts/:attemptId/exam', element: <ExamScreen /> }],
      },
    ],
  },

  /* ── After the exam ──────────────────────────────────────────────────
     Result, review and leaderboard are reached by students and guests alike,
     so they sit in their own block under `ExamRoute`. They wear the SAME site
     chrome as everything else: a guest who has just submitted is finished
     writing, and stripping the header and footer off their result left the
     page floating with no way back to the site and nothing identifying whose
     it is. The header and footer know about guests themselves and offer them
     only what a guest token can actually open. Everything else in the portal
     stays student-only below. */
  {
    element: <RouteBoundary where="exam-outcome" />,
    children: [
      {
        element: <ExamRoute />,
        children: [
          {
            element: <SiteLayout />,
            children: [
              { path: '/attempts/:attemptId/result', element: <ResultScreen /> },
              { path: '/attempts/:attemptId/review', element: <ReviewScreen /> },
              { path: '/tests/:testId/leaderboard', element: <LeaderboardScreen /> },
            ],
          },
        ],
      },
    ],
  },

  /* ── Student portal ──────────────────────────────────────────────────── */
  {
    element: <RouteBoundary where="student" />,
    children: [
      {
        element: <ProtectedRoute />,
        children: [
          {
            /* The same frame the public site uses — the portal differs by
               guard, not by chrome. */
            element: <SiteLayout />,
            children: [
              { path: '/dashboard', element: <LandingScreen /> },
              { path: '/profile', element: <ProfileScreen /> },
              { path: '/series/:seriesId', element: <SeriesDetailScreen /> },
              { path: '/series/:seriesId/tests/:testId/start', element: <TestStartScreen /> },
              { path: '/series/:seriesId/feedback', element: <FeedbackScreen /> },
            ],
          },
        ],
      },
    ],
  },

  /* ── Admin portal ────────────────────────────────────────────────────── */
  {
    element: <RouteBoundary where="admin" />,
    children: [
      {
        element: <AdminRoute />,
        children: [{ path: '/admin/*', element: <AdminApp /> }],
      },
    ],
  },


  { path: '*', element: <Navigate to="/" replace /> },
])
